import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserPlus, Loader2, AlertCircle } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import organizationService from '../../services/organization.service';

interface InviteMemberDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSuccess: () => void;
}

/**
 * Invite Member Dialog
 * Dialog for inviting new members to the organization
 */
const InviteMemberDialog: React.FC<InviteMemberDialogProps> = ({ open, onOpenChange, onSuccess }) => {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [isInviting, setIsInviting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const handleInvite = async () => {
        if (!email || !email.includes('@')) {
            setError(t('organization.members.emailRequired'));
            return;
        }

        try {
            setIsInviting(true);
            setError(null);
            setSuccess(false);

            await organizationService.inviteMember(email);

            setSuccess(true);
            setEmail('');

            // Close dialog after 2 seconds
            setTimeout(() => {
                onOpenChange(false);
                setSuccess(false);
                onSuccess();
            }, 2000);
        } catch (err: any) {
            console.error('Failed to invite member:', err);
            setError(err.response?.data?.error || err.message || t('organization.members.inviteFailed'));
        } finally {
            setIsInviting(false);
        }
    };

    const handleClose = () => {
        if (!isInviting) {
            setEmail('');
            setError(null);
            setSuccess(false);
            onOpenChange(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <UserPlus className="h-5 w-5" />
                        {t('organization.members.inviteTitle')}
                    </DialogTitle>
                    <DialogDescription>
                        {t('organization.members.inviteDescription')}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {error && (
                        <Alert variant="destructive">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    {success && (
                        <Alert className="border-green-500 bg-green-50 text-green-900">
                            <AlertDescription>{t('organization.members.inviteSent')}</AlertDescription>
                        </Alert>
                    )}

                    {!success && (
                        <>
                            <div className="space-y-2">
                                <Label htmlFor="email">{t('organization.members.emailLabel')}</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder={t('organization.members.emailPlaceholder')}
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleInvite();
                                        }
                                    }}
                                    disabled={isInviting}
                                    autoFocus
                                />
                            </div>

                            <Alert>
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription className="text-xs">
                                    {t('organization.members.requiresStytch')}
                                </AlertDescription>
                            </Alert>
                        </>
                    )}
                </div>

                {!success && (
                    <DialogFooter>
                        <Button variant="outline" onClick={handleClose} disabled={isInviting}>
                            {t('common.buttons.cancel')}
                        </Button>
                        <Button onClick={handleInvite} disabled={isInviting || !email}>
                            {isInviting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    {t('organization.members.inviting')}
                                </>
                            ) : (
                                <>
                                    <UserPlus className="mr-2 h-4 w-4" />
                                    {t('organization.members.sendInvitation')}
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default InviteMemberDialog;
